import { Language, SUPPORTED_LANGUAGES } from "./constants";

import en from "./locales/en.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import ar from "./locales/ar.json";
import pt from "./locales/pt.json";
import hi from "./locales/hi.json";
import sw from "./locales/sw.json";
import zh from "./locales/zh.json";
import yo from "./locales/yo.json";
import ig from "./locales/ig.json";
import ha from "./locales/ha.json";

export type { Language };
export { SUPPORTED_LANGUAGES, LANGUAGE_LABELS } from "./constants";

export interface Translation {
  [key: string]: any;
}

const translations: Record<Language, Translation> = {
  en,
  es,
  fr,
  zh,
  ar,
  pt,
  hi,
  sw,
  yo,
  ig,
  ha,
};

/**
 * Get translation value from nested object using dot notation
 * @param obj - Translation object
 * @param path - Dot-separated path (e.g., "common.save")
 * @param defaultValue - Fallback value if key not found
 */
export const getNestedTranslation = (
  obj: Translation,
  path: string,
  defaultValue: string = path
): string => {
  const keys = path.split(".");
  let value: any = obj;

  for (const key of keys) {
    if (value === null || value === undefined) {
      return defaultValue;
    }
    value = value[key];
  }

  // Ensure we return a string, not an object
  if (typeof value === "string") {
    return value;
  }
  
  // If value is an object or other non-string type, return defaultValue
  return defaultValue;
};

/**
 * Get all translations for a specific language
 */
export const getLanguageTranslations = (language: Language): Translation => {
  return translations[language] || translations.en;
};

/**
 * Get single translation by key and language
 */
export const translate = (
  key: string,
  language: Language,
  defaultValue?: string
): string => {
  const translationObj = getLanguageTranslations(language);
  return getNestedTranslation(translationObj, key, defaultValue || key);
};

/**
 * Batch translate multiple keys
 */
export const translateMultiple = (
  keys: string[],
  language: Language
): Record<string, string> => {
  const result: Record<string, string> = {};
  keys.forEach((key) => {
    result[key] = translate(key, language);
  });
  return result;
};

/**
 * Check if language is supported
 */
export const isLanguageSupported = (language: string): language is Language => {
  return SUPPORTED_LANGUAGES.includes(language as Language);
};

/**
 * Get browser language or fallback to default
 */
export const getBrowserLanguage = (): Language => {
  const browserLang = navigator.language.split("-")[0];
  if (isLanguageSupported(browserLang)) {
    return browserLang;
  }
  return "en";
};

/**
 * Map country code (ISO 3166-1 alpha-2) to supported language based on visitor location.
 * Returns null if country should use browser language or English.
 */
const COUNTRY_TO_LANGUAGE: Record<string, Language> = {
  cn: "zh",
  tw: "zh",
  hk: "zh",
  mo: "zh",
  sa: "ar",
  eg: "ar",
  ae: "ar",
  qa: "ar",
  kw: "ar",
  bh: "ar",
  om: "ar",
  jo: "ar",
  sy: "ar",
  iq: "ar",
  lb: "ar",
  ly: "ar",
  ma: "ar",
  tn: "ar",
  sd: "ar",
  es: "es",
  mx: "es",
  ar: "es",
  co: "es",
  pe: "es",
  ve: "es",
  cl: "es",
  ec: "es",
  gt: "es",
  cu: "es",
  bo: "es",
  do: "es",
  hn: "es",
  py: "es",
  sv: "es",
  ni: "es",
  cr: "es",
  pa: "es",
  uy: "es",
  fr: "fr",
  be: "fr",
  sn: "fr",
  ci: "fr",
  cm: "fr",
  ml: "fr",
  ne: "fr",
  bj: "fr",
  tg: "fr",
  cd: "fr",
  pt: "pt",
  br: "pt",
  ao: "pt",
  mz: "pt",
  in: "hi",
  tz: "sw",
  ke: "sw",
  ug: "sw",
  rw: "sw",
  bi: "sw",
  ng: "en",
  us: "en",
  gb: "en",
  au: "en",
  ca: "en",
  za: "en",
  gh: "en",
};

export const getLanguageFromCountry = (countryCode: string): Language | null => {
  const code = countryCode.toLowerCase();
  const lang = COUNTRY_TO_LANGUAGE[code];
  return lang && isLanguageSupported(lang) ? lang : null;
};

/**
 * Check if a language is RTL (Right-to-Left)
 */
export const isRTL = (language: Language): boolean => {
  const rtlLanguages: Language[] = ["ar"];
  return rtlLanguages.includes(language);
};
